import { useState, useEffect } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { motion } from "framer-motion"
import {
  CheckCircle, Clock, AlertTriangle, XCircle, CreditCard,
  Home, FileText, CalendarDays, User, Zap, Download,
} from "lucide-react"
import { useSubscription } from "@/hooks/useSubscription"
import { getPurchases, getStripePrice, type PurchaseRow, type StripePriceRow } from "@/lib/supabase"

const NAV_ITEMS = [
  { id: "home",     label: "Accueil",    Icon: Home,         path: "/dashboard" },
  { id: "sessions", label: "Séances",    Icon: FileText,     path: "/dashboard/sessions" },
  { id: "calendar", label: "Calendrier", Icon: CalendarDays, path: "/dashboard/calendar" },
  { id: "profile",  label: "Profil",     Icon: User,         path: "/dashboard/profile" },
]

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—"
  return new Date(iso).toLocaleDateString("fr-FR", {
    day: "numeric", month: "long", year: "numeric",
  })
}

export function BillingPage() {
  const navigate  = useNavigate()
  const location  = useLocation()

  const {
    subscription, status, isActive, trialDaysLeft, loading,
    startCheckout, openCustomerPortal,
  } = useSubscription()

  // Nom et montant lus depuis le catalogue `stripe_prices`, comme dans les Réglages :
  // trois autres offres arrivent et aucune ne doit demander un changement de code ici.
  const [price, setPrice]                 = useState<StripePriceRow | null>(null)
  const [purchases, setPurchases]         = useState<PurchaseRow[]>([])
  const [purchasesLoading, setPurchasesLoading] = useState(true)
  const [ctaLoading, setCtaLoading]       = useState(false)
  const [portalLoading, setPortalLoading] = useState(false)

  useEffect(() => {
    getPurchases()
      .then(setPurchases)
      .catch(() => {})
      .finally(() => setPurchasesLoading(false))
  }, [])

  useEffect(() => {
    if (!subscription?.price_id) return
    getStripePrice(subscription.price_id).then(setPrice).catch(() => null)
  }, [subscription?.price_id])

  const ebookPurchase = purchases.find((p) => p.product_id === "ebook")

  const planName = price?.product_name ?? "Ton accompagnement"

  // Le montant vient du catalogue, jamais du code : « 29,99 € » et « Coaching
  // Évolutif » étaient écrits en dur sur trois boutons de cette page, alors que
  // l'offre a changé de nom et que trois autres formules arrivent.
  const priceSuffix = price
    ? ` — ${(price.amount_cents / 100).toLocaleString("fr-FR", {
        minimumFractionDigits: price.amount_cents % 100 === 0 ? 0 : 2,
        maximumFractionDigits: 2,
      })} €/${price.interval === "year" ? "an" : "mois"}`
    : ""

  const handleCheckout = async () => {
    setCtaLoading(true)
    try { await startCheckout() } catch (err) { console.error(err) } finally { setCtaLoading(false) }
  }

  const handlePortal = async () => {
    setPortalLoading(true)
    try { await openCustomerPortal() } catch (err) { console.error(err) } finally { setPortalLoading(false) }
  }

  if (loading || purchasesLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#060a13" }}>
        <div className="w-8 h-8 rounded-full border-2 border-cyan-500/20 border-t-cyan-400 animate-spin" />
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-col pb-28" style={{ background: "#060a13" }}>

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        className="px-5 pt-12 pb-5"
      >
        <h1 className="text-[22px] font-bold text-white tracking-tight">Mon abonnement</h1>
        <p className="text-[13px] mt-0.5" style={{ color: "#64748b" }}>
          Statut et gestion de ton coaching
        </p>
      </motion.div>

      {/* Contenu */}
      <div className="flex-1 px-5 space-y-4">

        {/* ── Statut : Trial actif ── */}
        {status === "trialing" && isActive && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
            <div
              className="rounded-2xl p-5"
              style={{ background: "rgba(6,182,212,0.07)", border: "1px solid rgba(6,182,212,0.25)" }}
            >
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: "rgba(6,182,212,0.15)" }}>
                  <Clock className="w-5 h-5" style={{ color: "#06b6d4" }} />
                </div>
                <div>
                  <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold mb-1"
                    style={{ background: "rgba(6,182,212,0.2)", color: "#06b6d4" }}>
                    Semaine d'essai
                  </span>
                  <p className="text-[26px] font-extrabold leading-none" style={{ color: "#06b6d4" }}>
                    {trialDaysLeft} jour{trialDaysLeft > 1 ? "s" : ""}
                  </p>
                  <p className="text-white font-medium text-[13px] mt-0.5">restant{trialDaysLeft > 1 ? "s" : ""}</p>
                </div>
              </div>

              <p className="text-[12px] mb-4 leading-relaxed" style={{ color: "#94a3b8" }}>
                Accès complet jusqu'au matin du <strong style={{ color: "#e2e8f0" }}>{formatDate(subscription?.trial_ends_at)}</strong>.
                Aucune carte bancaire : rien ne sera prélevé à la fin de l'essai, tu choisiras toi-même la suite.
              </p>

              <button
                onClick={handleCheckout}
                disabled={ctaLoading}
                className="w-full py-3 rounded-xl font-semibold text-sm cursor-pointer transition-all duration-200 disabled:opacity-60"
                style={{
                  background: "linear-gradient(135deg, #06b6d4, #0891b2)",
                  color: "#fff",
                }}
              >
                {ctaLoading ? "Chargement…" : `S'abonner dès maintenant${priceSuffix}`}
              </button>
              <p className="text-center text-[11px] mt-2" style={{ color: "#64748b" }}>
                Tu peux aussi attendre la fin de ta semaine et décider à ce moment-là
              </p>
            </div>
          </motion.div>
        )}

        {/* ── Statut : Actif payant ── */}
        {status === "active" && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
            <div
              className="rounded-2xl p-5"
              style={{ background: "rgba(34,197,94,0.07)", border: "1px solid rgba(34,197,94,0.25)" }}
            >
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: "rgba(34,197,94,0.15)" }}>
                  <CheckCircle className="w-5 h-5" style={{ color: "#22c55e" }} />
                </div>
                <div className="flex-1">
                  <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold mb-1"
                    style={{ background: "rgba(34,197,94,0.2)", color: "#22c55e" }}>
                    Actif
                  </span>
                  <p className="text-white font-bold text-[15px]">{planName}</p>
                  <p className="text-[12px] mt-0.5" style={{ color: "#94a3b8" }}>
                    Renouvellement le <strong style={{ color: "#e2e8f0" }}>{formatDate(subscription?.current_period_end)}</strong>
                  </p>
                </div>
              </div>

              {subscription?.cancel_at_period_end && (
                <div className="mb-4 px-3 py-2.5 rounded-xl text-[12px] leading-snug"
                  style={{ background: "rgba(249,115,22,0.08)", color: "#f97316", border: "1px solid rgba(249,115,22,0.25)" }}>
                  <AlertTriangle className="w-3.5 h-3.5 inline mr-1.5 mb-0.5" />
                  Résiliation planifiée — accès garanti jusqu'au {formatDate(subscription.current_period_end)}
                </div>
              )}

              <button
                onClick={handlePortal}
                disabled={portalLoading}
                className="w-full py-3 rounded-xl font-semibold text-sm cursor-pointer transition-all duration-200 disabled:opacity-60"
                style={{
                  background: "rgba(34,197,94,0.12)",
                  color: "#22c55e",
                  border: "1px solid rgba(34,197,94,0.3)",
                }}
              >
                {portalLoading ? "Chargement…" : "Gérer mon abonnement →"}
              </button>
              <p className="text-center text-[11px] mt-2" style={{ color: "#64748b" }}>
                Factures · Modifier CB · Résilier
              </p>
            </div>
          </motion.div>
        )}

        {/* ── Statut : Paiement échoué / incomplet ── */}
        {(status === "past_due" || status === "incomplete") && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
            <div
              className="rounded-2xl p-5"
              style={{ background: "rgba(239,68,68,0.07)", border: "1px solid rgba(239,68,68,0.30)" }}
            >
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: "rgba(239,68,68,0.15)" }}>
                  <CreditCard className="w-5 h-5" style={{ color: "#ef4444" }} />
                </div>
                <div>
                  <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold mb-1"
                    style={{ background: "rgba(239,68,68,0.2)", color: "#ef4444" }}>
                    {status === "past_due" ? "Paiement en attente" : "Activation incomplète"}
                  </span>
                  <p className="text-white font-bold text-[15px]">
                    {status === "past_due" ? "Ton paiement a échoué" : "Abonnement non activé"}
                  </p>
                  <p className="text-[12px] mt-0.5 leading-snug" style={{ color: "#94a3b8" }}>
                    {status === "past_due"
                      ? "Mets à jour ton moyen de paiement pour maintenir l'accès."
                      : "Finalise ton paiement pour débloquer toutes les fonctionnalités."}
                  </p>
                </div>
              </div>
              <button
                onClick={handlePortal}
                disabled={portalLoading}
                className="w-full py-3 rounded-xl font-semibold text-sm cursor-pointer transition-all duration-200 disabled:opacity-60"
                style={{ background: "linear-gradient(135deg, #ef4444, #dc2626)", color: "#fff" }}
              >
                {portalLoading ? "Chargement…" : "Mettre à jour mon paiement"}
              </button>
            </div>
          </motion.div>
        )}

        {/* ── Statut : Résilié ou trial expiré ── */}
        {(status === "canceled" || (status === "trialing" && !isActive)) && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
            <div
              className="rounded-2xl p-5"
              style={{ background: "rgba(12,20,35,0.75)", border: "1px solid rgba(30,41,59,0.55)" }}
            >
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: "rgba(148,163,184,0.1)" }}>
                  <XCircle className="w-5 h-5" style={{ color: "#94a3b8" }} />
                </div>
                <div>
                  <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold mb-1"
                    style={{ background: "rgba(148,163,184,0.15)", color: "#94a3b8" }}>
                    {status === "canceled" ? "Résilié" : "Essai terminé"}
                  </span>
                  <p className="text-white font-bold text-[15px]">
                    {status === "canceled" ? "Abonnement résilié" : "Ta semaine d'essai est terminée"}
                  </p>
                  <p className="text-[12px] mt-0.5" style={{ color: "#64748b" }}>
                    {status === "canceled"
                      ? "Ton abonnement a pris fin."
                      : "Tes 7 jours sont écoulés. Ton bilan t'attend."}
                  </p>
                </div>
              </div>
              {/* Essai terminé : le bilan des 7 jours et les 4 offres, pas un
                  paiement sec. Un abonnement résilié, lui, se reprend directement. */}
              <button
                onClick={status === "canceled" ? handleCheckout : () => navigate("/fin-essai")}
                disabled={ctaLoading}
                className="w-full py-3 rounded-xl font-semibold text-sm cursor-pointer transition-all duration-200 disabled:opacity-60"
                style={{
                  background: "linear-gradient(135deg, #06b6d4, #0891b2)",
                  color: "#fff",
                  boxShadow: "0 0 20px rgba(6,182,212,0.25)",
                }}
              >
                {status === "canceled"
                  ? (ctaLoading ? "Chargement…" : `Reprendre mon accompagnement${priceSuffix}`)
                  : "Voir mon bilan et les offres"}
              </button>
            </div>
          </motion.div>
        )}

        {/* ── Statut : Pas d'abonnement (ebook / VIP / aucun) ── */}
        {status === null && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
            <div
              className="rounded-2xl p-5"
              style={{ background: "rgba(12,20,35,0.75)", border: "1px solid rgba(30,41,59,0.55)" }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: "rgba(6,182,212,0.1)" }}>
                  <Zap className="w-5 h-5" style={{ color: "#06b6d4" }} />
                </div>
                <div>
                  <p className="text-white font-semibold text-[15px]">Pas d'abonnement actif</p>
                  <p className="text-[12px] mt-0.5" style={{ color: "#64748b" }}>
                    Programme adaptatif, monitoring scientifique, nutrition.
                  </p>
                </div>
              </div>
              <button
                onClick={handleCheckout}
                disabled={ctaLoading}
                className="w-full py-3 rounded-xl font-semibold text-sm cursor-pointer transition-all duration-200 disabled:opacity-60"
                style={{ background: "linear-gradient(135deg, #06b6d4, #0891b2)", color: "#fff" }}
              >
                {ctaLoading ? "Chargement…" : `Découvrir ${planName}${priceSuffix}`}
              </button>
            </div>
          </motion.div>
        )}

        {/* ── Section ebook ── */}
        {ebookPurchase && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <div
              className="rounded-2xl p-5"
              style={{ background: "rgba(12,20,35,0.75)", border: "1px solid rgba(30,41,59,0.55)" }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: "rgba(139,92,246,0.15)" }}>
                  <Download className="w-5 h-5" style={{ color: "#8b5cf6" }} />
                </div>
                <div>
                  <p className="text-white font-semibold text-[15px]">E-book Personnalisé</p>
                  <p className="text-[12px] mt-0.5" style={{ color: "#64748b" }}>
                    Acheté le {formatDate(ebookPurchase.purchased_at)}
                  </p>
                </div>
              </div>
              <span title="Lien de téléchargement disponible prochainement">
                <button
                  disabled
                  className="w-full py-2.5 rounded-xl font-semibold text-sm cursor-not-allowed opacity-50"
                  style={{
                    background: "rgba(139,92,246,0.08)",
                    color: "#8b5cf6",
                    border: "1px solid rgba(139,92,246,0.2)",
                  }}
                >
                  Accéder à mon e-book
                </button>
              </span>
              <p className="text-center text-[11px] mt-2" style={{ color: "#64748b" }}>
                Lien de téléchargement envoyé par email après achat
              </p>
            </div>
          </motion.div>
        )}
      </div>

      {/* Bottom nav */}
      <nav
        className="fixed bottom-0 left-0 right-0 flex"
        style={{
          background: "rgba(6,10,19,0.95)",
          borderTop: "1px solid rgba(30,41,59,0.55)",
          backdropFilter: "blur(12px)",
        }}
      >
        {NAV_ITEMS.map(({ id, label, Icon: NavIcon, path }) => {
          const active =
            location.pathname === path ||
            (id !== "home" && location.pathname.startsWith(path))
          return (
            <button
              key={id}
              onClick={() => navigate(path)}
              className="flex-1 flex flex-col items-center gap-1 py-3 cursor-pointer transition-colors duration-150"
              style={{ color: active ? "#06b6d4" : "#64748b" }}
            >
              <NavIcon className="w-5 h-5" />
              <span className="text-[10px] font-medium">{label}</span>
            </button>
          )
        })}
      </nav>
    </div>
  )
}
